import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "@tanstack/react-router";

/**
 * Shared form protection: TanStack Router navigation blocking, a `beforeunload`
 * prompt, and a discard AlertDialog gate. The caller owns the dirtiness
 * computation and renders the dialog; this hook owns the lifecycle and pending
 * navigation.
 */
export function useFormDirtyGuard(isDirty: boolean) {
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const pendingDiscardAction = useRef<(() => void) | null>(null);
  const shouldBlock = useCallback(() => isDirty, [isDirty]);
  const blocker = useBlocker({
    shouldBlockFn: shouldBlock,
    enableBeforeUnload: isDirty,
    disabled: !isDirty,
    withResolver: true,
  });

  useEffect(() => {
    if (blocker.status !== "blocked") return;
    pendingDiscardAction.current = blocker.proceed;
    setShowDiscardDialog(true);
  }, [blocker.proceed, blocker.status]);

  useEffect(() => {
    if (isDirty) return;
    const action = pendingDiscardAction.current;
    if (!action) return;

    pendingDiscardAction.current = null;
    setShowDiscardDialog(false);
    action();
  }, [isDirty]);

  function requestDiscard(action: () => void) {
    pendingDiscardAction.current = action;
    setShowDiscardDialog(true);
  }

  /** Run `onClose` immediately when clean, or gate it behind the dialog. */
  function requestClose(onClose: () => void) {
    if (isDirty) {
      requestDiscard(onClose);
      return;
    }
    onClose();
  }

  function handleDiscardDialogOpenChange(open: boolean) {
    setShowDiscardDialog(open);
    if (open) return;
    pendingDiscardAction.current = null;
    if (blocker.status === "blocked") blocker.reset();
  }

  function confirmDiscard(fallback: () => void) {
    const action = pendingDiscardAction.current ?? fallback;
    pendingDiscardAction.current = null;
    setShowDiscardDialog(false);
    action();
  }

  return {
    showDiscardDialog,
    requestClose,
    confirmDiscard,
    handleDiscardDialogOpenChange,
  };
}
